import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AnalyticsViewConfig,
  analyticsViewConfigTypes,
  analyticsViewConfigFunnelType,
  analyticsViewConfigTableType,
} from "@hypertune/shared-internal/src/types";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useCreateAnalyticsViewMutation } from "../../../generated/graphql";
import { refetchAnalyticsViewsQueries } from "../../../lib/query/refetchQueries";
import { useSelectedAnalyticsViewId } from "./analyticsHooks";
import { setShowNewViewModal } from "./analyticsSlice";
import Modal from "../../../components/Modal";
import TextInput from "../../../components/input/TextInput";
import Label from "../../../components/Label";
import CardGroup, { CardGroupItem } from "../../../components/CardGroup";
import TypeIcon from "../../../components/icons/TypeIcon";
import { useHypertune } from "../../../generated/hypertune.react";

function NewViewModalInner({
  projectId,
}: {
  projectId: string;
}): React.ReactElement {
  const hypertune = useHypertune();

  const dispatch = useAppDispatch();

  const [, setAnalyticsViewId] = useSelectedAnalyticsViewId();

  const [name, setName] = useState("");

  const [config, setConfig] = useState<AnalyticsViewConfig>({
    type: analyticsViewConfigFunnelType,
    steps: [],
  });

  const [createView, { loading }] = useCreateAnalyticsViewMutation({
    refetchQueries: refetchAnalyticsViewsQueries,
    awaitRefetchQueries: true,
  });

  async function onSubmit(): Promise<void> {
    const trimmedName = name.trim();
    if (loading || !trimmedName) {
      return;
    }
    try {
      const result = await createView({
        variables: {
          input: {
            projectId,
            name: trimmedName,
            configJson: JSON.stringify(config),
          },
        },
      });
      const newViewId = result.data?.createAnalyticsView;
      if (newViewId) {
        setAnalyticsViewId(newViewId, true);
        dispatch(setShowNewViewModal(false));
      }
    } catch (error) {
      console.error("createAnalyticsView error:", error);
    }
  }

  return (
    <Modal
      title="Create new analytics view"
      onClose={() => dispatch(setShowNewViewModal(false))}
      saveText="Create"
      saveLoadingText="Creating..."
      saveLoading={loading}
      saveDisabled={name.trim() === ""}
      onSave={onSubmit}
      closeOnEsc
    >
      <div className="mt-4 flex flex-col text-tx-default">
        {hypertune.showNewAnalyticsViewTypeSelector({ fallback: false }) && (
          <>
            <Label type="title4" className="mb-[9px] text-tx-muted">
              Type
            </Label>
            <CardGroup
              className="mb-6"
              layout="grid"
              cardLayout="horizontal-with-icon"
              cards={analyticsViewConfigTypes.map(
                (type): CardGroupItem => ({
                  key: type,
                  className: "gap-2",
                  isSelected: type === config.type,
                  onClick: () =>
                    setConfig(
                      type === analyticsViewConfigFunnelType
                        ? { type: analyticsViewConfigFunnelType, steps: [] }
                        : { type: analyticsViewConfigTableType, steps: [] }
                    ),
                  children: (
                    <>
                      <TypeIcon
                        type={
                          type === analyticsViewConfigFunnelType
                            ? "funnel"
                            : "table"
                        }
                      />
                      <Label type="title3">
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Label>
                    </>
                  ),
                })
              )}
            />
          </>
        )}
        <Label type="title4" className="mb-[9px] text-tx-muted">
          Name
        </Label>
        <TextInput
          value={name}
          onChange={setName}
          focusOnMount
          trimOnBlur
          readOnly={loading}
          onEnter={onSubmit}
          placeholder={`Enter a name for this ${config.type.toLowerCase()}`}
        />
      </div>
    </Modal>
  );
}

export default function NewViewModal(): React.ReactElement | null {
  const showNewViewModal = useAppSelector(
    (state) => state.projectAnalytics.showNewViewModal
  );
  const { selectedProjectId } = useParams();

  if (!showNewViewModal || !selectedProjectId) {
    return null;
  }

  return <NewViewModalInner projectId={selectedProjectId} />;
}
